import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, PhoneIcon, MailIcon, XIcon } from 'lucide-react';
import { useStudio } from '../../contexts/StudioContext';
import { vehicleSizes } from '../../data/schedule';
import { Booking } from '../../types/booking';
import { slotsForDate } from '../../utils/availability';
import { duration, euro, longDate, shortDate, timeRange } from '../../utils/format';
import { PaymentBadge, StatusBadge } from './StatusBadge';
type Props = {
  booking: Booking;
  onClose: () => void;
};
export function BookingDetail({
  booking,
  onClose
}: Props) {
  const {
    services,
    extras,
    bookings,
    settings,
    setStatus,
    setPaymentStatus,
    reschedule
  } = useStudio();
  const [moveDate, setMoveDate] = useState(booking.date);
  const [moved, setMoved] = useState(false);
  const [copied, setCopied] = useState(false);
  const moveSlots = useMemo(() => slotsForDate(moveDate, booking.durationMinutes, bookings, settings), [moveDate, booking.durationMinutes, bookings, settings]);
  const service = services.find(s => s.id === booking.serviceId);
  const chosen = extras.filter(e => booking.extraIds.includes(e.id));
  const size = vehicleSizes.find(v => v.id === booking.vehicleSizeId);
  return <div className="fixed inset-0 z-50 flex items-end justify-center lg:items-stretch lg:justify-end" data-id="element-187" data-mp="3de9e3betgneu">
      <button type="button" aria-label="Sluiten" onClick={onClose} className="absolute inset-0 bg-void/70 backdrop-blur-sm" data-id="element-188" data-mp="3hbfb7de77twp" />
      <motion.aside initial={{
      y: 40,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.24,
      ease: [0.23, 1, 0.32, 1]
    }} className="safe-bottom relative flex max-h-[92vh] w-full max-w-md flex-col overflow-y-auto rounded-t-3xl border border-edge bg-carbon lg:h-full lg:max-h-none lg:rounded-none lg:border-0 lg:border-l" role="dialog" aria-label={`Boeking ${booking.reference}`} data-id="element-189" data-mp="3ramvyln9iqtu">
        <span className="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full bg-edge lg:hidden" aria-hidden="true" data-id="element-190" data-mp="29zwol3qfw0ce" />
        <div className="flex items-start justify-between gap-4 border-b border-edge px-6 py-5" data-id="element-191" data-mp="25zvyumxw42q1">
          <div data-id="element-192" data-mp="2jqrdygyzfg22">
            <button type="button" onClick={() => {
            navigator.clipboard?.writeText(booking.reference);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
          }} className="text-xs uppercase tracking-[0.16em] text-dim transition-colors duration-200 ease-smooth hover:text-ember" data-id="element-193" data-mp="2jrajszbcydzq">
              {copied ? 'Referentie gekopieerd' : `${booking.reference} · kopieer`}
            </button>
            <h2 className="mt-1.5 font-display text-xl font-extrabold tracking-tight text-chalk" data-id="element-194" data-mp="2xqk2q6qy7m7e">
              {booking.customer.name}
            </h2>
            <p className="mt-1 text-sm text-dim" data-id="element-195" data-mp="13cj2pijnwt5q">
              {service?.name} · {size?.label}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Sluiten" className="flex h-9 w-9 items-center justify-center rounded-full border border-edge text-dim transition-colors duration-200 ease-smooth hover:text-chalk" data-id="element-196" data-mp="3ezmlcuc4jnxj">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-197" data-mp="3a7bc30vhupsb" />
          </button>
        </div>

        <div className="flex flex-wrap gap-2 px-6 py-4" data-id="element-198" data-mp="2w7k0f6sg9lhh">
          <StatusBadge status={booking.status} data-id="element-199" data-mp="317oih190ao9t" />
          <PaymentBadge status={booking.paymentStatus} data-id="element-200" data-mp="318hgndtwg5gc" />
        </div>

        <dl className="divide-y divide-edge border-y border-edge text-sm" data-id="element-201" data-mp="2n8f1hzcz7q4s">
          <div className="flex justify-between gap-4 px-6 py-3.5" data-id="element-202" data-mp="2na9l9acihc7a">
            <dt className="text-dim" data-id="element-203" data-mp="2ra0ju08a4wmd">Wanneer</dt>
            <dd className="text-right font-medium capitalize text-chalk" data-id="element-204" data-mp="lgtodvptz13v">
              {longDate(booking.date)} · {timeRange(booking.time, booking.durationMinutes)}
            </dd>
          </div>
          <div className="flex justify-between gap-4 px-6 py-3.5" data-id="element-205" data-mp="r7sut2q6mft">
            <dt className="text-dim" data-id="element-206" data-mp="peus266hajf">Duur</dt>
            <dd className="text-right font-medium text-chalk" data-id="element-207" data-mp="3rxiyz3mlede0">{duration(booking.durationMinutes)}</dd>
          </div>
          <div className="flex justify-between gap-4 px-6 py-3.5" data-id="element-208" data-mp="3mxei3chrvgn9">
            <dt className="text-dim" data-id="element-209" data-mp="3i04jp7cenuna">Voertuig</dt>
            <dd className="text-right font-medium text-chalk" data-id="element-210" data-mp="3hzcz0rkcjhbs">
              {booking.customer.car}
              {booking.customer.plate ? ` · ${booking.customer.plate}` : ''}
            </dd>
          </div>
          {chosen.length > 0 && <div className="flex justify-between gap-4 px-6 py-3.5" data-id="element-211" data-mp="347ye2f6vp623">
              <dt className="text-dim" data-id="element-212" data-mp="3463qet482zub">Extra's</dt>
              <dd className="text-right font-medium text-chalk" data-id="element-213" data-mp="2za6csukcg7l8">{chosen.map(e => e.name).join(', ')}</dd>
            </div>}
          <div className="flex justify-between gap-4 px-6 py-3.5" data-id="element-214" data-mp="2u98tufrif9io">
            <dt className="text-dim" data-id="element-215" data-mp="2q9hzy36osd9x">Aanbetaling</dt>
            <dd className="text-right font-medium text-chalk" data-id="element-216" data-mp="2qat44441nn1k">
              {booking.paymentMethod === 'deposit' ? euro(booking.depositAmount) : '—'}
            </dd>
          </div>
          <div className="flex justify-between gap-4 px-6 py-3.5" data-id="element-217" data-mp="19u2q6goxk1my">
            <dt className="text-dim" data-id="element-218" data-mp="1ese0esj50e5p">Openstaand</dt>
            <dd className="text-right font-medium text-chalk" data-id="element-219" data-mp="1jq5ofbgnvg4m">
              {booking.paymentStatus === 'paid' ? euro(0) : euro(booking.paymentStatus === 'deposit_paid' ? booking.amount - booking.depositAmount : booking.amount)}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-4 bg-panel px-6 py-4" data-id="element-220" data-mp="vyi7nmd45lg2">
            <dt className="text-dim" data-id="element-221" data-mp="10smdu3ek1pg3">Totaal</dt>
            <dd className="font-display text-xl font-extrabold text-ember" data-id="element-222" data-mp="3cfjy3lmqpug">{euro(booking.amount)}</dd>
          </div>
        </dl>

        {booking.customer.notes && <p className="border-b border-edge px-6 py-4 text-sm leading-relaxed text-dim" data-id="element-223" data-mp="88ntn1h25qu8">
            “{booking.customer.notes}”
          </p>}

        <div className="flex gap-2 px-6 py-4" data-id="element-224" data-mp="3sd2acy1rhjju">
          <a href={`tel:${booking.customer.phone.replace(/\s/g, '')}`} className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-edge px-4 py-3 text-sm text-chalk transition-colors duration-200 ease-smooth hover:border-ember hover:text-ember" data-id="element-225" data-mp="3scals76l0m32">
            <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-226" data-mp="39f97twv1vbmz" />
            Bellen
          </a>
          <a href={`mailto:${booking.customer.email}`} className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-edge px-4 py-3 text-sm text-chalk transition-colors duration-200 ease-smooth hover:border-ember hover:text-ember" data-id="element-227" data-mp="3nf0qiawihg7b">
            <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-228" data-mp="xpox9l48u848" />
            Mailen
          </a>
        </div>

        <div className="mt-auto space-y-4 border-t border-edge px-6 py-5" data-id="element-229" data-mp="2fnl8wsdhg0yr">
          <div data-id="element-230" data-mp="2fpoa88k35gis">
            <h3 className="text-xs uppercase tracking-[0.2em] text-dim" data-id="element-231" data-mp="2kmem0jouxpye">Verzetten</h3>
            <input type="date" value={moveDate} aria-label="Nieuwe datum" onChange={e => {
            setMoveDate(e.target.value);
            setMoved(false);
          }} className="mt-3 w-full rounded-xl border border-edge bg-panel px-3.5 py-2.5 text-sm text-chalk focus:border-ember" data-id="element-232" data-mp="2kk205vfzavg2" />
            {moveSlots.length > 0 ? <div className="mt-2 grid grid-cols-4 gap-2" data-id="element-233" data-mp="2pm11z0w2lkaw">
                {moveSlots.map(slot => <button key={slot.time} type="button" onClick={() => {
              reschedule(booking.id, moveDate, slot.time);
              setMoved(true);
            }} className={`rounded-lg border px-1 py-2 text-xs transition-colors duration-200 ease-smooth ${booking.date === moveDate && booking.time === slot.time ? 'border-ember bg-ember text-void' : slot.available ? 'border-edge bg-panel text-chalk hover:border-ember/50' : 'border-edge/60 bg-panel text-dim/50'}`} data-id="element-234" data-mp="2uk2nrtkvrxq2">
                    {slot.time}
                  </button>)}
              </div> : <p className="mt-2 text-xs text-dim" data-id="element-235" data-mp="20wluwe7aexzv">Gesloten op deze datum — kies een andere dag.</p>}
            {moved && <p className="mt-2 text-xs text-ember" data-id="element-236" data-mp="1ry0eoppafv29">Afspraak verzet.</p>}
          </div>

          <div data-id="element-237" data-mp="kk6rgyebh1d4">
            <h3 className="text-xs uppercase tracking-[0.2em] text-dim" data-id="element-238" data-mp="kkokzn9lh1hc">Status</h3>
            <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-239" data-mp="gil9wzqu7ywl">
              {(['pending', 'confirmed', 'completed', 'cancelled'] as const).map(status => <button key={status} type="button" onClick={() => setStatus(booking.id, status)} className={`rounded-xl border px-3 py-2.5 text-xs transition-colors duration-200 ease-smooth ${booking.status === status ? 'border-ember bg-ember text-void' : 'border-edge bg-panel text-chalk hover:border-ember/50'}`} data-id="element-240" data-mp="2s7hyfom399vc">
                  {status === 'pending' ? 'Aanvraag' : status === 'confirmed' ? 'Bevestigen' : status === 'completed' ? 'Afronden' : 'Annuleren'}
                </button>)}
            </div>
          </div>

          <div data-id="element-241" data-mp="1ui6kz41djxlz">
            <h3 className="text-xs uppercase tracking-[0.2em] text-dim" data-id="element-242" data-mp="1uhmzjdcimr37">Betaling</h3>
            <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-243" data-mp="11qlaf4qsb8vc">
              {(['unpaid', 'deposit_paid', 'paid', 'refunded'] as const).map(status => <button key={status} type="button" onClick={() => setPaymentStatus(booking.id, status)} className={`inline-flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2.5 text-xs transition-colors duration-200 ease-smooth ${booking.paymentStatus === status ? 'border-ember bg-ember text-void' : 'border-edge bg-panel text-chalk hover:border-ember/50'}`} data-id="element-244" data-mp="16q6iq5rugtkq">
                  {booking.paymentStatus === status && <CheckCircle2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-245" data-mp="1zdk7hgle2itr" />}
                  {status === 'unpaid' ? 'Open' : status === 'deposit_paid' ? 'Aanbetaald' : status === 'paid' ? 'Betaald' : 'Terugbetaald'}
                </button>)}
            </div>
          </div>

          <p className="text-xs text-dim" data-id="element-246" data-mp="3jvuph5v5e2tv">Aangemaakt {shortDate(booking.createdAt.slice(0, 10))} · online</p>
        </div>
      </motion.aside>
    </div>;
}